import React, { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, FlaskConical, Play } from "lucide-react";
import { MaskText } from "@/components/motion/Reveal";
import VideoLightbox from "@/components/site/VideoLightbox";

const EASE = [0.22, 1, 0.36, 1];

// White-label manufacturing process montage: lab -> blend -> fill -> final product.
// Close-up clips (no faces). New AI clips fall back to a working clip until generated.
const CLIPS = [
  {
    sources: [
      "/media/laboratorio.mp4",
      "https://videos.pexels.com/video-files/8863360/8863360-hd_1920_1080_30fps.mp4",
    ],
    label: "Laboratorio",
  },
  {
    sources: ["/media/mezcla.mp4", "/media/envasado.mp4"],
    label: "Mezcla",
  },
  {
    sources: ["/media/produccion.mp4"],
    label: "Envasado",
  },
  {
    sources: ["/media/etiquetado.mp4", "/media/produccion.mp4"],
    label: "Producto final",
  },
];
const HERO_POSTER =
  "https://images.pexels.com/photos/3735709/pexels-photo-3735709.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Hero() {
  const sectionRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yVideo = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleVideo = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const opacityContent = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const particles = useMemo(
    () =>
      Array.from({ length: 22 }, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 12,
        duration: 10 + Math.random() * 12,
        size: 3 + Math.random() * 5,
      })),
    []
  );

  return (
    <section id="top" ref={sectionRef} className="relative overflow-hidden bg-white">
      {/* Parallax video background */}
      <motion.div className="hero-video-sim" style={{ y: yVideo, scale: scaleVideo }}>
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          data-testid="hero-video-0"
        >
          {CLIPS[0].sources.map((s) => (
            <source key={s} src={s} type="video/mp4" />
          ))}
        </video>

        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/45 to-white/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-transparent to-white/30" />
        <div className="absolute inset-0 bg-[#EFF6FF]/20" />

        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />
        <div className="hero-blob hero-blob-3" />
        <div className="absolute inset-0 grid-lines-light opacity-60" />

        {particles.map((p, i) => (
          <span
            key={i}
            className="hero-particle"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              width: `${p.size}px`,
              height: `${p.size}px`,
            }}
          />
        ))}
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: yContent, opacity: opacityContent }}
        className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-12 md:pt-32 md:pb-16"
      >
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 border border-[#0A84E2]/30 bg-[#0A84E2]/10 px-3 py-1.5 rounded-full text-xs font-semibold text-[#086AB7] uppercase tracking-widest backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#0A84E2] animate-pulse" />
            <Play className="w-3 h-3" fill="currentColor" />
            Fabricante español · Arganda del Rey (Madrid)
          </motion.div>

          <h1 className="mt-8 font-display font-extrabold text-[#0A1628] text-[1.9rem] sm:text-[2.5rem] lg:text-[3.2rem] xl:text-[3.85rem] uppercase leading-[0.95] tracking-tighter">
            <MaskText
              delay={0.15}
              lines={[
                "Fabricante de",
                <span key="l2" className="text-[#0A84E2]">nutrición deportiva</span>,
                "marca blanca",
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 max-w-2xl text-lg text-slate-600 leading-relaxed"
          >
            Somos <span className="text-[#0A1628] font-semibold">Global Nutralabs</span>, fabricante español de complementos alimenticios y laboratorio I+D. Desarrollamos, formulamos y fabricamos tu marca —propia o de terceros (B2B)— bajo estándares <span className="text-[#0A1628] font-semibold">GMP auditados</span> y con <span className="text-[#0A1628] font-semibold">registro FDA</span> para exportar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              href="#contacto"
              data-testid="hero-cta-primary"
              className="btn-brand rounded-sm px-7 py-4 text-sm font-semibold inline-flex items-center gap-2 uppercase tracking-wider"
            >
              Hablemos de tu proyecto
              <ArrowRight className="w-4 h-4" />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              href="#servicios"
              data-testid="hero-cta-secondary"
              className="btn-outline rounded-sm px-7 py-4 text-sm font-semibold inline-flex items-center gap-2 uppercase tracking-wider"
            >
              Conocer servicios
            </motion.a>
            <motion.button
              type="button"
              onClick={() => setVideoOpen(true)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-video-open-button"
              aria-label="Ver vídeo corporativo"
              className="group inline-flex items-center gap-3 rounded-sm border border-[#0A84E2]/40 bg-white/80 backdrop-blur-sm pl-1.5 pr-5 py-1.5 transition-colors duration-300 hover:border-[#0A84E2] hover:bg-white"
            >
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#0A84E2] text-white shadow-lg shadow-[#0A84E2]/40 transition-transform duration-300 group-hover:scale-110">
                <span className="absolute inset-0 rounded-full bg-[#0A84E2]/50 animate-ping [animation-duration:2.2s]" />
                <Play className="relative w-4 h-4 ml-0.5" fill="currentColor" />
              </span>
              <span className="text-left leading-tight">
                <span className="block text-sm font-semibold uppercase tracking-wider text-[#0A1628]">Ver vídeo</span>
                <span className="block text-xs text-slate-500 font-medium">Conoce nuestra planta · 1 min</span>
              </span>
            </motion.button>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.9 } } }}
            className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-4xl"
          >
            <Stat value="+4.500 m²" label="instalaciones" />
            <Stat value="2021" label="año de fundación" />
            <Stat value="+1.000" label="referencias activas" />
            <Stat value="GMP·FDA" label="certificaciones" />
            <Stat value="B2B" label="propia y terceros" />
          </motion.div>
        </div>

        <div className="hidden xl:flex flex-col gap-3 absolute right-12 bottom-8">
          <FloatingBadge icon={<ShieldCheck className="w-4 h-4" />} label="GMP auditado · Registro FDA" delay={1.1} />
          <FloatingBadge icon={<FlaskConical className="w-4 h-4" />} label="Departamento I+D propio" delay={1.25} />
        </div>
      </motion.div>

      <VideoLightbox open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}>
      <div className="font-display text-3xl md:text-4xl font-bold text-[#0A1628] whitespace-nowrap">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
    </motion.div>
  );
}

function FloatingBadge({ icon, label, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-slate-200 shadow-sm px-4 py-2 rounded-sm text-xs text-slate-700 font-medium"
    >
      <span className="text-[#0A84E2]">{icon}</span>
      {label}
    </motion.div>
  );
}
